
import React from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import HomeIcon from '@mui/icons-material/Home';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { emphasize, styled } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import { useContext, useEffect, useState } from 'react';
import { FaCloudUploadAlt } from "react-icons/fa";
import Button from '@mui/material/Button';
import { deleteData, deleteImages, editData, fetchDataFromApi, uploadImage } from '../../utils/api';
import { useNavigate, useParams } from 'react-router-dom';
import { MyContext } from '../../App';
import CircularProgress from '@mui/material/CircularProgress';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { FaRegImages } from "react-icons/fa";
import { IoCloseSharp } from "react-icons/io5";
import * as MuiIcons from "@mui/icons-material";

// Breadcrumb styling
const StyledBreadcrumb = styled(Chip)(({ theme }) => {
    const backgroundColor =
        theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[800];
    return {
        backgroundColor,
        height: theme.spacing(3),
        color: theme.palette.text.primary,
        fontWeight: theme.typography.fontWeightRegular,
        '&:hover, &:focus': {
            backgroundColor: emphasize(backgroundColor, 0.06),
        },
        '&:active': {
            boxShadow: theme.shadows[1],
            backgroundColor: emphasize(backgroundColor, 0.12),
        },
    };
});

const EditSubCat = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [catData, setCatData] = useState([]);
    const [categoryVal, setcategoryVal] = useState('');
    const [isImageRemove, setIsImageRemove] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);

    const [formFields, setFormFields] = useState({
        name: '',
        slug: '',
        parentId: '',
        images: [],
        bannerImages: [],
        bannerVideos: [],
        color: '',
        icon: '',
        themeColor: ''
    });

    const [previews, setPreviews] = useState([]);
    const [bannerPreviews, setBannerPreviews] = useState([]);
    const [videoPreviews, setVideoPreviews] = useState([]);

    const formdata = new FormData();
    const bannerFormdata = new FormData();
    const videoFormdata = new FormData();

    const { id } = useParams();
    const history = useNavigate();
    const context = useContext(MyContext);

    // Popular icon options for subcategories
    const popularIcons = [
        "LocalGroceryStore",
        "Restaurant",
        "LocalDining",
        "Icecream",
        "AcUnit",
        "LocalBar",
        "Cake",
        "Fastfood",
        "LocalPizza",
        "RamenDining",
        "LunchDining",
        "Egg",
        "Coffee",
        "LocalCafe",
        "Liquor",
        "SoupKitchen",
        "RiceBowl",
        "EmojiFoodBeverage",
        "Breakfast",
        "Dinner",
        "FoodBank",
        "TakeoutDining",
        "KebabDining",
        "Tapas",
        "Bento",
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
        context.setProgress(20);

        // Fetch all categories for dropdown and find subcategory
        fetchDataFromApi('/api/category').then((res) => {
            setCatData(res);

            // Function to find subcategory from nested structure
            const findSubCategory = (categories) => {
                if (!categories || !categories.categoryList) return null;

                for (let category of categories.categoryList) {
                    // Check if the main category matches
                    if (category._id === id) {
                        return category;
                    }
                    // Check in children (subcategories)
                    if (category.children && category.children.length > 0) {
                        const found = category.children.find(child => child._id === id);
                        if (found) return found;
                    }
                }
                return null;
            };

            const subCat = findSubCategory(res);

            if (subCat) {
                // Populate form fields with existing data
                setFormFields({
                    name: subCat.name || '',
                    slug: subCat.slug || '',
                    parentId: subCat.parentId || '',
                    images: subCat.images || [],
                    bannerImages: subCat.bannerImages || [],
                    bannerVideos: subCat.bannerVideos || [],
                    color: subCat.color || '#ffffff',
                    icon: subCat.icon || '',
                    themeColor: subCat.themeColor || '#ffffff'
                });

                setcategoryVal(subCat.parentId || '');

                // Set existing images as previews
                if (subCat.images && subCat.images.length > 0) {
                    const existingPreviews = subCat.images.map((img, index) => ({
                        id: `existing-${index}`,
                        img: img,
                        isExisting: true
                    }));
                    setPreviews(existingPreviews);
                }

                // Set existing banner images as previews
                if (subCat.bannerImages && subCat.bannerImages.length > 0) {
                    const existingBannerPreviews = subCat.bannerImages.map((img, index) => ({
                        id: `existing-banner-${index}`,
                        img: img,
                        isExisting: true
                    }));
                    setBannerPreviews(existingBannerPreviews);
                }

                // Set existing videos as previews
                if (subCat.bannerVideos && subCat.bannerVideos.length > 0) {
                    const existingVideoPreviews = subCat.bannerVideos.map((vid, index) => ({
                        id: `existing-video-${index}`,
                        video: vid,
                        isExisting: true
                    }));
                    setVideoPreviews(existingVideoPreviews);
                }

                setDataLoaded(true);
                context.setProgress(100);
            } else {
                context.setAlertBox({
                    open: true,
                    error: true,
                    msg: "Subcategory not found!",
                });
                context.setProgress(100);
                history('/subCategory');
            }
        }).catch((error) => {
            console.error("Error fetching data:", error);
            context.setAlertBox({
                open: true,
                error: true,
                msg: "Failed to load subcategory data!",
            });
            context.setProgress(100);
        });

        // Clean up temporary uploads
        fetchDataFromApi("/api/imageUpload").then((res) => {
            res?.map((item) => {
                item?.images?.map((img) => {
                    deleteImages(`/api/category/deleteImage?img=${img}`).then(() => {
                        deleteData("/api/imageUpload/deleteAllImages");
                    });
                });
            });
        });

        fetchDataFromApi("/api/bannerUpload").then((res) => {
            res?.map((item) => {
                item?.images?.map((img) => {
                    deleteImages(`/api/category/deleteImage?img=${img}`).then(() => {
                        deleteData("/api/bannerUpload/deleteAllImages");
                    });
                });
            });
        });

        fetchDataFromApi("/api/videoUpload").then((res) => {
            res?.map((item) => {
                item?.videos?.map((vid) => {
                    deleteImages(`/api/category/deleteVideo?video=${vid}`).then(() => {
                        deleteData("/api/videoUpload/deleteAllVideos");
                    });
                });
            });
        });
    }, [id]);

    const changeInput = (e) => {
        setFormFields(() => ({
            ...formFields,
            [e.target.name]: e.target.value
        }));
    };

    const handleChangeCategory = (event) => {
        setcategoryVal(event.target.value);
        setFormFields(prev => ({
            ...prev,
            parentId: event.target.value
        }));
    };

    let img_arr = [];
    let banner_arr = [];
    let video_arr = [];
    let uniqueArray = [];

    // Get icon component by name
    const getIconComponent = (iconName) => {
        const IconComponent = MuiIcons[iconName];
        return IconComponent ? <IconComponent /> : null;
    };

    // Subcategory icon/thumbnail upload
    const onChangeFile = async (e, apiEndPoint) => {
        try {
            const files = e.target.files;
            const selectedImages = [];

            setUploading(true);

            for (var i = 0; i < files.length; i++) {
                if (
                    files[i] &&
                    (files[i].type === "image/jpeg" ||
                        files[i].type === "image/jpg" ||
                        files[i].type === "image/png" ||
                        files[i].type === "image/webp")
                ) {
                    const file = files[i];
                    selectedImages.push(file);
                    formdata.append(`images`, file);
                } else {
                    context.setAlertBox({
                        open: true,
                        error: true,
                        msg: "Please select a valid JPG or PNG image file.",
                    });

                    setUploading(false);
                    return false;
                }
            }
        } catch (error) {
            console.log(error);
        }

        uploadImage(apiEndPoint, formdata).then((res) => {
            fetchDataFromApi("/api/imageUpload").then((response) => {
                if (
                    response !== undefined &&
                    response !== null &&
                    response !== "" &&
                    response.length !== 0
                ) {
                    response.length !== 0 &&
                        response.map((item) => {
                            item?.images.length !== 0 &&
                                item?.images?.map((img) => {
                                    img_arr.push({
                                        id: item?._id,
                                        img: img,
                                        isExisting: false
                                    });
                                });
                        });

                    uniqueArray = img_arr.filter(
                        (item, index, self) =>
                            index === self.findIndex((t) => t.img === item.img)
                    );

                    const appendedArray = [...previews, ...uniqueArray];
                    setPreviews(appendedArray);

                    setTimeout(() => {
                        setUploading(false);
                        context.setAlertBox({
                            open: true,
                            error: false,
                            msg: "Images Uploaded!",
                        });
                    }, 200);
                }
            });
        });
    };

    // Banner images upload
    const onChangeBannerFile = async (e, apiEndPoint) => {
        try {
            const files = e.target.files;
            const selectedBanners = [];

            setUploading(true);

            for (var i = 0; i < files.length; i++) {
                if (
                    files[i] &&
                    (files[i].type === "image/jpeg" ||
                        files[i].type === "image/jpg" ||
                        files[i].type === "image/png" ||
                        files[i].type === "image/webp")
                ) {
                    const file = files[i];
                    selectedBanners.push(file);
                    bannerFormdata.append(`images`, file);
                } else {
                    context.setAlertBox({
                        open: true,
                        error: true,
                        msg: "Please select a valid JPG or PNG image file.",
                    });

                    setUploading(false);
                    return false;
                }
            }
        } catch (error) {
            console.log(error);
        }

        uploadImage(apiEndPoint, bannerFormdata).then((res) => {
            fetchDataFromApi("/api/bannerUpload").then((response) => {
                if (
                    response !== undefined &&
                    response !== null &&
                    response !== "" &&
                    response.length !== 0
                ) {
                    response.length !== 0 &&
                        response.map((item) => {
                            item?.images.length !== 0 &&
                                item?.images?.map((img) => {
                                    banner_arr.push({
                                        id: item?._id,
                                        img: img,
                                        isExisting: false
                                    });
                                });
                        });

                    const uniqueBanners = banner_arr.filter(
                        (item, index, self) =>
                            index === self.findIndex((t) => t.img === item.img)
                    );

                    const appendedBanners = [...bannerPreviews, ...uniqueBanners];
                    setBannerPreviews(appendedBanners);

                    setTimeout(() => {
                        setUploading(false);
                        context.setAlertBox({
                            open: true,
                            error: false,
                            msg: "Banner Images Uploaded!",
                        });
                    }, 200);
                }
            });
        });
    };

    // Video upload
    const onChangeVideoFile = async (e, apiEndPoint) => {
        try {
            const files = e.target.files;
            const selectedVideos = [];

            setUploading(true);

            for (var i = 0; i < files.length; i++) {
                if (
                    files[i] &&
                    (files[i].type === "video/mp4" ||
                        files[i].type === "video/webm" ||
                        files[i].type === "video/ogg")
                ) {
                    const file = files[i];
                    selectedVideos.push(file);
                    videoFormdata.append(`videos`, file);
                } else {
                    context.setAlertBox({
                        open: true,
                        error: true,
                        msg: "Please select a valid video file (MP4, WebM, OGG).",
                    });

                    setUploading(false);
                    return false;
                }
            }
        } catch (error) {
            console.log(error);
        }

        uploadImage(apiEndPoint, videoFormdata).then((res) => {
            fetchDataFromApi("/api/videoUpload").then((response) => {
                if (
                    response !== undefined &&
                    response !== null &&
                    response !== "" &&
                    response.length !== 0
                ) {
                    response.length !== 0 &&
                        response.map((item) => {
                            item?.videos.length !== 0 &&
                                item?.videos?.map((vid) => {
                                    video_arr.push({
                                        id: item?._id,
                                        video: vid,
                                        isExisting: false
                                    });
                                });
                        });

                    const uniqueVideos = video_arr.filter(
                        (item, index, self) =>
                            index === self.findIndex((t) => t.video === item.video)
                    );

                    const appendedVideos = [...videoPreviews, ...uniqueVideos];
                    setVideoPreviews(appendedVideos);

                    setTimeout(() => {
                        setUploading(false);
                        context.setAlertBox({
                            open: true,
                            error: false,
                            msg: "Videos Uploaded!",
                        });
                    }, 200);
                }
            });
        });
    };

    const removeImg = async (indexToRemove, imgUrl, img_id, isExisting) => {
        setIsImageRemove(true);

        if (isExisting) {
            // Remove from existing images
            const updatedPreviews = previews.filter((_, index) => index !== indexToRemove);
            setPreviews(updatedPreviews);
            setIsImageRemove(false);
            
            context.setAlertBox({
                open: true,
                error: false,
                msg: "Image will be removed on update!",
            });
        } else {
            // Remove newly uploaded image
            deleteImages(`/api/category/deleteImage?img=${imgUrl}`).then((res) => {
                context.setAlertBox({
                    open: true,
                    error: false,
                    msg: "Image Deleted!",
                });

                deleteData(`/api/imageUpload/deleteImage/image/${img_id}`).then(
                    (resp) => {
                        fetchDataFromApi("/api/imageUpload").then((response) => {
                            const img_array = [];
                            response?.map((item) => {
                                img_array.push({
                                    id: item?._id,
                                    img: item?.images[0],
                                    isExisting: false
                                });
                            });
                            
                            const existingImages = previews.filter(p => p.isExisting);
                            setPreviews([...existingImages, ...img_array]);
                            setIsImageRemove(false);
                        });
                    }
                );
            });
        }
    };

    const removeBannerImg = async (indexToRemove, imgUrl, img_id, isExisting) => {
        setIsImageRemove(true);

        if (isExisting) {
            const updatedPreviews = bannerPreviews.filter((_, index) => index !== indexToRemove);
            setBannerPreviews(updatedPreviews);
            setIsImageRemove(false);
            
            context.setAlertBox({
                open: true,
                error: false,
                msg: "Banner will be removed on update!",
            });
        } else {
            deleteImages(`/api/category/deleteImage?img=${imgUrl}`).then((res) => {
                context.setAlertBox({
                    open: true,
                    error: false,
                    msg: "Banner Image Deleted!",
                });

                deleteData(`/api/bannerUpload/deleteImage/image/${img_id}`).then(
                    (resp) => {
                        fetchDataFromApi("/api/bannerUpload").then((response) => {
                            const img_array = [];
                            response?.map((item) => {
                                img_array.push({
                                    id: item?._id,
                                    img: item?.images[0],
                                    isExisting: false
                                });
                            });
                            
                            const existingBanners = bannerPreviews.filter(p => p.isExisting);
                            setBannerPreviews([...existingBanners, ...img_array]);
                            setIsImageRemove(false);
                        });
                    }
                );
            });
        }
    };

    const removeVideo = async (indexToRemove, videoUrl, video_id, isExisting) => {
        setIsImageRemove(true);

        if (isExisting) {
            const updatedPreviews = videoPreviews.filter((_, index) => index !== indexToRemove);
            setVideoPreviews(updatedPreviews);
            setIsImageRemove(false);
            
            context.setAlertBox({
                open: true,
                error: false,
                msg: "Video will be removed on update!",
            });
        } else {
            deleteImages(`/api/category/deleteVideo?video=${videoUrl}`).then((res) => {
                context.setAlertBox({
                    open: true,
                    error: false,
                    msg: "Video Deleted!",
                });

                deleteData(`/api/videoUpload/deleteVideo/video/${video_id}`).then(
                    (resp) => {
                        fetchDataFromApi("/api/videoUpload").then((response) => {
                            const video_array = [];
                            response?.map((item) => {
                                video_array.push({
                                    id: item?._id,
                                    video: item?.videos[0],
                                    isExisting: false
                                });
                            });
                            
                            const existingVideos = videoPreviews.filter(p => p.isExisting);
                            setVideoPreviews([...existingVideos, ...video_array]);
                            setIsImageRemove(false);
                        });
                    }
                );
            });
        }
    };

    const updateSubCategory = (e) => {
        e.preventDefault();

        formFields.slug = formFields.name;
        
        // Collect all image URLs (both existing and new)
        const allImages = previews.map(p => p.img);
        const allBanners = bannerPreviews.map(p => p.img);
        const allVideos = videoPreviews.map(p => p.video);

        const updatedFormFields = {
            ...formFields,
            images: allImages,
            bannerImages: allBanners,
            bannerVideos: allVideos
        };

        if (updatedFormFields.name !== "" && updatedFormFields.parentId !== "") {
            setIsLoading(true);

            editData(`/api/category/${id}`, updatedFormFields).then((res) => {
                setIsLoading(false);
                context.fetchCategory();

                deleteData("/api/imageUpload/deleteAllImages");
                deleteData("/api/bannerUpload/deleteAllImages");
                deleteData("/api/videoUpload/deleteAllVideos");

                context.setAlertBox({
                    open: true,
                    error: false,
                    msg: 'Sub Category Updated Successfully!'
                });

                setTimeout(() => {
                    history('/subCategory');
                }, 1500);
            }).catch((err) => {
                setIsLoading(false);
                context.setAlertBox({
                    open: true,
                    error: true,
                    msg: 'Failed to update subcategory!'
                });
            });
        } else {
            context.setAlertBox({
                open: true,
                error: true,
                msg: 'Please fill all the required details'
            });
            return false;
        }
    };

    if (!dataLoaded) {
        return (
            <div className="right-content w-100">
                <div className="card shadow border-0 w-100 flex-row p-4 mt-2 align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                    <CircularProgress />
                    <span className="ml-3">Loading Sub Category Data...</span>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="right-content w-100">
                <div className="card shadow border-0 w-100 flex-row p-4 mt-2">
                    <h5 className="mb-0">Edit Sub Category</h5>
                    <Breadcrumbs aria-label="breadcrumb" className="ml-auto breadcrumbs_">
                        <StyledBreadcrumb
                            component="a"
                            href="#"
                            label="Dashboard"
                            icon={<HomeIcon fontSize="small" />}
                        />

                        <StyledBreadcrumb
                            component="a"
                            label="Sub Category"
                            href="#"
                            deleteIcon={<ExpandMoreIcon />}
                        />
                        <StyledBreadcrumb
                            label="Edit Sub Category"
                            deleteIcon={<ExpandMoreIcon />}
                        />
                    </Breadcrumbs>
                </div>

                <form className='form' onSubmit={updateSubCategory}>
                    <div className='row'>
                        <div className='col-sm-9'>
                            <div className='card p-4 mt-0'>
                                <div className='form-group'>
                                    <h6>Parent Category</h6>
                                    <Select
                                        value={categoryVal}
                                        onChange={handleChangeCategory}
                                        displayEmpty
                                        inputProps={{ 'aria-label': 'Without label' }}
                                        className='w-100'
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {
                                            catData?.categoryList?.length !== 0 && catData?.categoryList?.map((cat) => {
                                                return (
                                                    <MenuItem 
                                                        className="text-capitalize" 
                                                        value={cat._id} 
                                                        key={cat._id}
                                                    >
                                                        {cat.name}
                                                    </MenuItem>
                                                )
                                            })
                                        }
                                    </Select>
                                </div>

                                <div className='form-group'>
                                    <h6>Sub Category Name</h6>
                                    <input type='text' name='name' value={formFields.name} onChange={changeInput} />
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <h6>Card Background Color</h6>
                                            <input
                                                type="color"
                                                name="color"
                                                value={formFields.color}
                                                onChange={changeInput}
                                                style={{ width: "100%", height: "40px" }}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <h6>Theme Color (Product Listing Page)</h6>
                                            <input
                                                type="color"
                                                name="themeColor"
                                                value={formFields.themeColor}
                                                onChange={changeInput}
                                                style={{ width: "100%", height: "40px" }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <h6>Sub Category Icon</h6>
                                    <FormControl fullWidth>
                                        <InputLabel>Select Icon</InputLabel>
                                        <Select
                                            name="icon"
                                            value={formFields.icon}
                                            onChange={changeInput}
                                            label="Select Icon"
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {popularIcons.map((iconName) => (
                                                <MenuItem key={iconName} value={iconName}>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "10px",
                                                        }}
                                                    >
                                                        {getIconComponent(iconName)}
                                                        <span>{iconName}</span>
                                                    </div>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    {formFields.icon && (
                                        <div
                                            style={{
                                                marginTop: "10px",
                                                padding: "10px",
                                                background: formFields.color || "#f5f5f5",
                                                borderRadius: "8px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "10px",
                                            }}
                                        >
                                            <span>Preview:</span>
                                            {getIconComponent(formFields.icon)}
                                        </div>
                                    )}
                                </div>

                                <div className="imagesUploadSec">
                                    <h5 className="mb-4">Sub Category Icon/Thumbnail Image</h5>

                                    <div className="imgUploadBox d-flex align-items-center">
                                        {previews?.length !== 0 &&
                                            previews?.map((img, index) => {
                                                return (
                                                    <div className="uploadBox" key={index}>
                                                        <span
                                                            className="remove"
                                                            disabled={isImageRemove === true ? true : false}
                                                            onClick={() => removeImg(index, img?.img, img?.id, img?.isExisting)}
                                                        >
                                                            <IoCloseSharp />
                                                        </span>
                                                        <div className="box">
                                                            <LazyLoadImage
                                                                alt={"image"}
                                                                effect="blur"
                                                                className="w-100"
                                                                src={img?.img}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        <div className="uploadBox">
                                            {uploading === true ? (
                                                <div className="progressBar text-center d-flex align-items-center justify-content-center flex-column">
                                                    <CircularProgress />
                                                    <span>Uploading...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <input
                                                        type="file"
                                                        onChange={(e) =>
                                                            onChangeFile(e, "/api/category/upload")
                                                        }
                                                        name="images"
                                                    />
                                                    <div className="info">
                                                        <FaRegImages />
                                                        <h5>image upload</h5>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="imagesUploadSec mt-4">
                                    <h5 className="mb-4">Product Listing Page Banners (Images)</h5>

                                    <div className="imgUploadBox d-flex align-items-center">
                                        {bannerPreviews?.length !== 0 &&
                                            bannerPreviews?.map((img, index) => {
                                                return (
                                                    <div className="uploadBox" key={index}>
                                                        <span
                                                            className="remove"
                                                            disabled={isImageRemove === true ? true : false}
                                                            onClick={() =>
                                                                removeBannerImg(index, img?.img, img?.id, img?.isExisting)
                                                            }
                                                        >
                                                            <IoCloseSharp />
                                                        </span>
                                                        <div className="box">
                                                            <LazyLoadImage
                                                                alt={"banner"}
                                                                effect="blur"
                                                                className="w-100"
                                                                src={img?.img}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        <div className="uploadBox">
                                            {uploading === true ? (
                                                <div className="progressBar text-center d-flex align-items-center justify-content-center flex-column">
                                                    <CircularProgress />
                                                    <span>Uploading...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <input
                                                        type="file"
                                                        multiple
                                                        onChange={(e) =>
                                                            onChangeBannerFile(e, "/api/banner/upload")
                                                        }
                                                        name="bannerImages"
                                                    />
                                                    <div className="info">
                                                        <FaRegImages />
                                                        <h5>banner upload</h5>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="imagesUploadSec mt-4">
                                    <h5 className="mb-4">Product Listing Page Videos</h5>

                                    <div className="imgUploadBox d-flex align-items-center">
                                        {videoPreviews?.length !== 0 &&
                                            videoPreviews?.map((vid, index) => {
                                                return (
                                                    <div className="uploadBox" key={index}>
                                                        <span
                                                            className="remove"
                                                            disabled={isImageRemove === true ? true : false}
                                                            onClick={() =>
                                                                removeVideo(index, vid?.video, vid?.id, vid?.isExisting)
                                                            }
                                                        >
                                                            <IoCloseSharp />
                                                        </span>
                                                        <div className="box">
                                                            <video
                                                                controls
                                                                className="w-100"
                                                                src={vid?.video}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        <div className="uploadBox">
                                            {uploading === true ? (
                                                <div className="progressBar text-center d-flex align-items-center justify-content-center flex-column">
                                                    <CircularProgress />
                                                    <span>Uploading...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <input
                                                        type="file"
                                                        multiple
                                                        accept="video/*"
                                                        onChange={(e) =>
                                                            onChangeVideoFile(e, "/api/video/upload")
                                                        }
                                                        name="bannerVideos"
                                                    />
                                                    <div className="info">
                                                        <FaRegImages />
                                                        <h5>video upload</h5>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <br />

                                <Button type="submit" className="btn-blue btn-lg btn-big w-100">
                                    <FaCloudUploadAlt /> &nbsp;{" "}
                                    {isLoading === true ? (
                                        <CircularProgress color="inherit" className="loader" />
                                    ) : (
                                        "UPDATE SUB CATEGORY"
                                    )}{" "}
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </>
    );
};

export default EditSubCat;

